import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { EmptyState, fmtMoney } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Trash, ArrowCounterClockwise, Warning, Clock, User } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatDeletedAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function daysUntilExpiry(expire_at) {
  if (!expire_at) return null;
  try {
    const then = new Date(expire_at).getTime();
    const now = Date.now();
    return Math.max(0, Math.ceil((then - now) / (1000 * 60 * 60 * 24)));
  } catch {
    return null;
  }
}

export default function TrashPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients/deleted/list");
      setItems(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const restore = async (id, name) => {
    if (!confirm(`¿Restaurar el cliente "${name}"?\n\nEl cliente volverá a la lista activa con todos sus datos originales.`)) return;
    setBusyId(id);
    try {
      await api.post(`/clients/deleted/${id}/restore`);
      toast.success(`"${name}" restaurado a clientes activos`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const purge = async (id, name) => {
    if (!confirm(`¿Eliminar DEFINITIVAMENTE a "${name}"?\n\nEsta acción NO se puede deshacer y elimina todos los datos del cliente de la papelera antes de los 30 días de retención.`)) return;
    setBusyId(id);
    try {
      await api.delete(`/clients/deleted/${id}`);
      toast.success("Cliente eliminado definitivamente");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <PageShell
      section="Operación"
      page="Papelera"
      context={`${items.length} en papelera`}
      testid="trash-toolbar"
    >
      {/* Info banner */}
      <div className="mb-5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md px-4 py-3 flex items-start gap-3">
        <Clock size={16} className="text-slate-500 mt-0.5 shrink-0" />
        <div className="text-[13px] text-slate-600 dark:text-slate-300 leading-relaxed">
          Los clientes eliminados permanecen en la papelera durante <strong>30 días</strong>.
          Puedes restaurarlos con todos sus datos originales o eliminarlos definitivamente.
          Pasados 30 días se purgan automáticamente.
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Cargando…</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Trash}
            title="Papelera vacía"
            message="Los clientes eliminados aparecerán aquí. Puedes restaurarlos hasta 30 días después de eliminarlos."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trash-table">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Cliente</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Dirección</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Tarifa</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Eliminado</th>
                  <th className="text-center px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Purga en</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const days = daysUntilExpiry(c.expire_at);
                  const isUrgent = days !== null && days <= 5;
                  return (
                    <tr key={c.id} data-testid={`trash-row-${c.id}`} className="border-b border-slate-100 dark:border-slate-900 hover:bg-slate-50/50 dark:hover:bg-slate-900/40">
                      <td className="px-4 py-3 align-middle">
                        <div className="font-semibold text-slate-900 dark:text-slate-100">{c.name || "—"}</div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          {c.email || c.phone || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-middle text-slate-700 dark:text-slate-300 max-w-[280px]">
                        <div className="text-[13px] truncate">{c.address || "—"}</div>
                        {(c.city || c.zip_code) && (
                          <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                            {[c.city, c.state, c.zip_code].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 align-middle text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
                        {fmtMoney(c.monthly_fee)}
                      </td>
                      <td className="px-3 py-3 align-middle text-[12px] text-slate-600 dark:text-slate-400">
                        <div className="flex items-center gap-1.5">
                          <User size={11} className="text-slate-400" />
                          {c.deleted_by || "—"}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                          {formatDeletedAt(c.deleted_at)}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-middle text-center">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold tabular-nums font-mono ${
                          isUrgent ? "text-rose-700 dark:text-rose-400" : "text-slate-600 dark:text-slate-300"
                        }`}>
                          {isUrgent && <Warning size={11} weight="fill" />}
                          {days === null ? "—" : `${days}d`}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-middle text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button
                            onClick={() => restore(c.id, c.name)}
                            disabled={busyId === c.id}
                            data-testid={`trash-restore-${c.id}`}
                            className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <ArrowCounterClockwise size={12} className="mr-1" />
                            Restaurar
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => purge(c.id, c.name)}
                            disabled={busyId === c.id}
                            data-testid={`trash-purge-${c.id}`}
                            className="h-8 text-[11px] text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                            title="Eliminar definitivamente"
                          >
                            <Trash size={12} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PageShell>
  );
}
